<template>
  <vf-input
    v-bind="{ disabled, invalid, required, variant }"
    data-test-id="input-phone"
  >
    <slot>{{ $t.mobileNumber }}</slot>
    <template #input>
      <base-input
        v-bind="{ disabled, placeholder, required }"
        ref="el"
        v-model="phone"
        type="tel"
        @blur="$emit('change', $event)"
        @change="$emit('change', $event)"
      />
    </template>
    <template v-if="!hideCodeSelect" #select>
      <base-popover
        v-slot="{ close, open, side }"
        :arrow-shift="-6"
        class="flex"
        flip
        :offset="{ mainAxis: 10, alignmentAxis: -20 }"
        placement="top"
      >
        <span>
          <base-select
            v-model="localCode"
            :aria-labelledby="id"
            class="form-input"
            :class="{ 'bg-grey-90 b-grey-50': disabled }"
            data-test-id="form-input-phone-select"
            :disabled="disableCodeSelect || disabled"
            :label-formatter="labelFormatter"
            :options
            @blur="close"
            @focus="open"
            @keydown.esc="close"
            @mouseenter="open"
            @mouseleave="close"
          />
          <vf-icon class="ml-2" dir="down" name="chevron" />
        </span>
        <base-popover-content
          :id
          class="z-overlay ws-nowrap b b-grey-70 bg-white px-4 py-6 text-sm shadow-xs transition"
          data-test-id="vf-tooltip-content"
          enter-active-class="transform ease-out"
          enter-from-class="op-0 scale-95"
          enter-to-class="translate-y-0 scale-100"
          leave-active-class="transform ease-in"
          leave-from-class="translate-y-0 scale-100"
          leave-to-class="op-0 scale-95"
          role="tooltip"
          style="max-width: 15rem"
        >
          {{ $t.mobileCountryCode }}
          <base-popover-arrow
            class="absolute transform b-grey-70 bg-white"
            :class="side === 'top' ? 'b-b b-r' : 'b-t b-l'"
            style="width: 0.625rem; height: 0.625rem; rotate: 45deg"
          />
        </base-popover-content>
      </base-popover>
    </template>
    <template #end>
      <slot name="end" />
    </template>
  </vf-input>
</template>

<script lang="ts" setup>
import type { SelectOption } from '#types/components/base/select'
import type { CountryCode } from '#types/locale'
import phoneCodes from 'assets/phoneCodes.json'

const { addressCountryCode, showCountryCode } = defineProps<{
  addressCountryCode?: string
  disabled?: boolean
  disableCodeSelect?: boolean
  hideCodeSelect?: boolean
  invalid?: boolean
  placeholder?: string
  required?: boolean
  showCountryCode?: boolean
  variant?: 'inverse'
}>()

defineEmits<{ change: [value: string] }>()

const model = defineModel<string>({ required: true })
const code = defineModel<string>('code')

const id = useId()

const countryCode = computed<Uppercase<CountryCode>>(
  () => code.value
    ? getCountryCodeByPhoneCode(code.value, addressCountryCode || useCountryCode()) as Uppercase<CountryCode>
    : useCountryCode()
)

const codes = phoneCodes.map(({ name, dialCode }) => ({
  label: `${name} (+${dialCode})`,
  value: dialCode
}))

const codeCounts = phoneCodes.reduce((acc, { dialCode }) => {
  if (!acc[dialCode]) acc[dialCode] = 0
  acc[dialCode]++
  return acc
}, {})

const codesWithCountryCode = phoneCodes.map(({ name, dialCode, code }) => ({
  label: `${name} (+${dialCode})`,
  value: codeCounts[dialCode] > 1 ? `${dialCode}-${code}` : dialCode,
  dialCode
}))

const options = showCountryCode ? codesWithCountryCode : codes

const labelFormatter = (v?: SelectOption) => `+${(v?.value as string).split('-')[0] || ''}`

const localCode = computed({
  get() {
    return showCountryCode && code.value && codeCounts[code.value] > 1
      ? `${code.value}-${countryCode.value}`
      : code.value
  },
  set(event) {
    code.value = (event?.split('-') || [])[0]
  }
})

const { value: phone, clearValue, el } = useMask((value) => {
  const mask = getPhoneMaskByPhoneCode(localCode.value ?? '1', countryCode.value)
  if (!mask) return ''
  if (typeof mask === 'string') return mask
  const phoneLength = Array.from(value.matchAll(/\d/g)).length
  // In case our phoneCode for the locale is an array of maskes with the different lengths, i.e. LU ['XXX - XXX', 'XXX - XXX - XXX'],
  // we want to default to the one with the longest length rather than the first item in the array.
  const mostXsItem = mask.reduce((maxItem, currentItem) => {
    const numXs = (currentItem.match(/X/g) || []).length
    return numXs > (maxItem.match(/X/g) || []).length ? currentItem : maxItem
  }, '')

  return mask.find((submask) => phoneLength === submask.match(/X/g)?.length) || mostXsItem
}, {
  regex: /\d/,
  onUpdate: (v) => model.value = v
})

watch(
  [model, el],
  (_value) => {
    if (el.value)
      clearValue.value = model.value
  },
  { immediate: true }
)
</script>
